import { i, s, x, A } from '../../lit-element.js';
import { o } from '../../class-map.js';
import { reset } from '../../styles/reset.style.js';
import { getFocusableElements } from '../../utils/get-focusable-elements.js';
import '../../directive.js';

const style = i`
  .dialog {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    opacity: 0;
    position: fixed;
    right: 0;
    top: 0;
    transition: opacity 0.25s ease-in, visibility 0.25s;
    visibility: hidden;
    width: 100%;
    z-index: 999;
  }

  .dialog.active {
    opacity: 1;
    visibility: visible;
  }

  .dialog-overlay {
    background-color: hsla(0, 0%, 100%, 0.9);
    bottom: 0;
    height: 100%;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: 100%;
  }

  .dialog-overlay-dark {
    background-color: rgba(20, 20, 20, 0.9);
  }

  .dialog-header {
    display: -ms-grid;
    display: grid;
    -ms-grid-columns: 1fr auto 1fr;
    grid-template-columns: 1fr auto 1fr;
  }

  .dialog-left-slot {
    align-self: center;
    -ms-grid-column: 1;
    grid-column: 1 / 2;
    -ms-grid-column-align: start;
    -ms-grid-column-span: 1;
    -ms-grid-row-align: center;
    justify-self: left;
  }

  .dialog-right-slot {
    align-self: center;
    -ms-grid-column: 3;
    grid-column: 3 / 4;
    -ms-grid-column-align: end;
    -ms-grid-column-span: 1;
    -ms-grid-row-align: center;
    justify-self: right;
    padding-right: 0.625em;
  }

  .dialog-container {
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
    box-sizing: border-box;
    display: none;
    max-height: 96vh;
    max-width: 30em;
    overflow: auto;
    padding: 1em;
    position: relative;
  }

  .dialog.active .dialog-container {
    display: flex;
    flex: 0 1 30em;
    flex-direction: column;
  }

  @media only screen and (max-width: 599px) {
    .dialog-container {
      border-radius: 0;
      bottom: 0;
      box-shadow: none;
      flex: 1 1 auto;
      height: 100%;
      height: calc(var(--vh, 1vh) * 100);
      left: 0;
      max-width: 100vw;
      min-height: 100vh;
      min-height: calc(var(--vh, 1vh) * 100);
      right: 0;
      top: 0;
    }
  }

  .dialog-close {
    background-color: transparent;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='25' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.748.174l10.668 10.668L23.084.174l1.023 1.022-10.669 10.668 10.669 10.669-1.023 1.022-10.668-10.668L1.748 23.555.726 22.533l10.668-10.669L.726 1.196z' fill='%23141414'/%3E%3C/svg%3E");
    border: 0;
    cursor: pointer;
    font-size: 0;
    height: 24px;
    right: 24px;
    top: 24px;
    width: 24px;
  }

  .dialog-content {
    margin-top: 0.5em;
    padding: 0 1em;
  }

  .dialog-button-row {
    display: flex;
    justify-content: flex-end;
  }
`;

class RefiModal extends s {
  constructor() {
    super();
    this.closeEv = new CustomEvent('close-modal', {
      composed: true,
      bubbles: true,
    });
    this.handleEsc = ev => {
      if (ev.which === 27) {
        this.closeModal(ev);
      }
    };
    this.sendToBottom = ev => {
      if (ev.which === 9 && ev.shiftKey) {
        ev.preventDefault();
        this.lastElem.focus();
      }
    };
    this.sendToTop = ev => {
      if (ev.which === 9 && !ev.shiftKey) {
        ev.preventDefault();
        this.firstElem.focus();
      }
    };
    this.handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    this.showBack = false;
  }

  static get properties() {
    return {
      light: { type: Boolean, attribute: true },
      active: { type: Boolean, attribute: true, reflect: true },
      showBack: { type: Boolean, attribute: 'show-back', reflect: true },
      ariaLabelledByTitle: { type: String, attribute: 'modal-title', reflect: true },
      ariaLabel: { type: String, attribute: 'modal-label', reflect: true },
    };
  }

  static get styles() {
    return [reset, style];
  }

  render() {
    return x`
      <div
        class="dialog ${o({ active: this.active })}"
        @transitionend="${this.toggleFocusLock}"
        aria-labelledby="${this.ariaLabelledByTitle || A}"
        aria-label="${this.ariaLabel || A}"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <div class="dialog-overlay ${o({ 'dialog-overlay-dark': !this.light })}" @click="${this.closeModal}"></div>
        <div class="dialog-container">
          <div class="dialog-header">
            <div class="dialog-left-slot">
              <refi-common-back-button ?hidden="${!this.showBack}" class="back-button"></refi-common-back-button>
            </div>
            <div class="dialog-right-slot">
              <button type="button" class="dialog-close" @click="${this.closeModal}" aria-label="close"></button>
            </div>
          </div>
          <div class="dialog-content">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleEsc);
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleEsc);
    window.removeEventListener('resize', this.handleResize);
    document.body.style.overflow = 'visible';
    this.removeFocusLock();
    super.disconnectedCallback();
  }

  firstUpdated() {
    const modalElems = Array.from(getFocusableElements(this.shadowRoot.querySelector('.dialog-container')));
    const slottedElems = this.shadowRoot
      .querySelector('slot')
      .assignedElements({ flatten: true })
      .flatMap(node => Array.from(getFocusableElements(node)));
    const [firstElem, ...rest] = modalElems.concat(slottedElems);
    // setting the first element to be the close button when back button is disabled
    // so that focus lock isn't broken looking for a hidden element
    if (!this.showBack) {
      const [, secondEl] = modalElems;
      this.firstElem = secondEl;
    } else {
      this.firstElem = firstElem;
    }
    this.lastElem = rest.pop() || firstElem;
  }

  addFocusLock() {
    this.firstUpdated();
    this.shadowRoot.querySelector('.dialog').focus();
    this.firstElem.addEventListener('keydown', this.sendToBottom);
    this.lastElem.addEventListener('keydown', this.sendToTop);
  }

  removeFocusLock() {
    if (this.firstElem && this.lastElem) {
      this.firstElem.removeEventListener('keydown', this.sendToBottom);
      this.lastElem.removeEventListener('keydown', this.sendToBottom);
    }
  }

  toggleFocusLock(ev) {
    if (ev.propertyName === 'visibility' && this.active) {
      document.body.style.overflow = 'hidden';
      this.addFocusLock();
    }
    if (ev.propertyName === 'visibility' && !this.active) {
      document.body.style.overflow = 'visible';
      this.removeFocusLock();
    }
  }

  closeModal(ev) {
    ev.preventDefault();
    this.active = false;
    this.dispatchEvent(this.closeEv);
  }
}

export { RefiModal };
